import React from "react";
import { Table, Input, Pagination, Select, Spin, Tooltip } from "antd";
import { FaSearch } from "react-icons/fa";
import { RiDeleteBinLine } from "react-icons/ri";
import { LoadingOutlined } from "@ant-design/icons";
import { FiEdit2 } from "react-icons/fi";
import "./table.css";
import { MdOutlineNotificationsActive } from "react-icons/md";
import { IoDuplicateOutline } from "react-icons/io5";

const { Search } = Input;

/** Safely get nested value from object using string ("a.b.c") or array path */
const getByPath = (obj, path) => {
  if (!obj) return undefined;
  if (Array.isArray(path))
    return path.reduce((o, k) => (o ? o[k] : undefined), obj);
  if (typeof path === "string" && path.includes(".")) {
    return path.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);
  }
  return obj?.[path];
};

/** Basic comparator that handles strings, numbers, dates */
const baseCompare = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  // Date strings
  const aDate =
    typeof a === "string" && !isNaN(Date.parse(a)) ? new Date(a) : null;
  const bDate =
    typeof b === "string" && !isNaN(Date.parse(b)) ? new Date(b) : null;
  if (aDate && bDate) return aDate - bDate;

  // Numbers
  if (typeof a === "number" && typeof b === "number") return a - b;

  // Fallback to string compare (case-insensitive)
  return String(a).localeCompare(String(b), undefined, {
    sensitivity: "base",
    numeric: true,
  });
};

/* =========================
   Deep search helpers
   ========================= */

// Strip HTML tags (best-effort)
const stripHtml = (s) => String(s).replace(/<[^>]*>/g, " ");

// Normalize to lower-case, remove diacritics, collapse whitespace
const normalize = (s) =>
  stripHtml(String(s ?? ""))
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

// Recursively collect primitive-ish values from any object/array
const collectDeepValues = (val, out) => {
  if (val == null) return;
  const t = typeof val;
  if (t === "string" || t === "number" || t === "boolean") {
    out.push(String(val));
    return;
  }
  if (Array.isArray(val)) {
    for (const item of val) collectDeepValues(item, out);
    return;
  }
  if (t === "object") {
    for (const k of Object.keys(val)) collectDeepValues(val[k], out);
  }
};

// console.log(collectDeepValues)

// Build a searchable text blob per row (includes deep values + column values + simple render results)
// Build a searchable text blob per row (with include/exclude support)
const buildRowSearchText = (row, columns, { includeFields, excludeFields }) => {
  const parts = [];

  // Helper: should we include a dataIndex in search?
  const shouldIncludeKey = (key) => {
    if (!key && key !== 0) return false;
    const k = String(key);
    if (includeFields && includeFields.length) {
      return includeFields.some((inc) => String(inc) === k);
    }
    if (excludeFields && excludeFields.length) {
      return !excludeFields.some((exc) => String(exc) === k);
    }
    return true; // no include/exclude → include by default
  };

  // Collect from columns' dataIndex and simple render outputs
  columns.forEach((col) => {
    const di = col.dataIndex;
    if (di !== undefined && shouldIncludeKey(di)) {
      const v = getByPath(row, di);
      if (v != null) parts.push(v);
    }

if (typeof col.render === "function") {
  try {
    const v = di !== undefined ? getByPath(row, di) : undefined;
    const rendered = col.render(v, row);

    const pushIfOk = (txt) => {
      if (
        (typeof txt === "string" || typeof txt === "number") &&
        shouldIncludeKey(col.key ?? di)
      ) {
        parts.push(String(txt));
      }
    };

    // direct string/number
    pushIfOk(rendered);

    // handle simple React elements and arrays of children
    if (rendered && typeof rendered === "object" && rendered.props) {
      const kids = rendered.props.children;
      const collect = (k) => {
        if (Array.isArray(k)) k.forEach(collect);
        else if (k != null && (typeof k === "string" || typeof k === "number")) {
          parts.push(String(k));
        }
      };
      collect(kids);
    }
  } catch {
    /* ignore */
  }
}

  });

  return normalize(parts.join(" | "));
};

const TableOne = ({
  columns,
  data,
  handleEdit,
  handleDelete,
  handleAddSlide,
  pageName,
  loading,
  handleSendNotification,
  rowSelection,
  handleCopyChapter,
  rowKeyField = "id",

  /** Optional: pass custom sorters: { columnKey: (a,b) => number } */
  customSorters = {},
  /** If true, auto-enable sorter on columns with dataIndex and no custom render */
  enableAutoSort = true,
  searchExcludeFields = ["description"],
  searchIncludeFields,
  searchWholeWord = true,
  leftExtra,
}) => {
  const [searchText, setSearchText] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);

  // controlled sort state (AntD uses 'ascend' | 'descend' | null)
  const [sortField, setSortField] = React.useState(null);
  const [sortOrder, setSortOrder] = React.useState(null); // 'ascend' | 'descend' | null

  const spinnerIcon = (
    <LoadingOutlined style={{ fontSize: 40, color: "#251120" }} spin />
  );

  // ---- helpers (add once near other helpers) ----
const extractTextFromNode = (node, out) => {
  if (node == null) return;
  if (typeof node === "string" || typeof node === "number") {
    out.push(String(node));
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((n) => extractTextFromNode(n, out));
    return;
  }
  if (node && node.props && "children" in node.props) {
    extractTextFromNode(node.props.children, out);
  }
};

// ---- REPLACE your buildRowSearchText with this ----
const buildRowSearchText = (row, columns, { includeFields, excludeFields }) => {
  const parts = [];

  // 1) Always include explicit includeFields directly from the row (additive)
  if (Array.isArray(includeFields) && includeFields.length) {
    for (const f of includeFields) {
      const v = getByPath(row, f);
      if (v != null) parts.push(v);
    }
  }

  // helper: only exclude if explicitly listed in excludeFields
  const shouldIncludeKey = (key) => {
    if (!key && key !== 0) return true;
    const k = String(key);
    if (excludeFields && excludeFields.length) {
      return !excludeFields.some((exc) => String(exc) === k);
    }
    return true;
  };

  // 2) Collect from columns (dataIndex + deep render output)
  columns.forEach((col, idx) => {
    const di = col.dataIndex;
    const key = col.key ?? di ?? `col-${idx}`;

    if (di !== undefined && shouldIncludeKey(di)) {
      const v = getByPath(row, di);
      if (v != null) parts.push(v);
    }

    if (typeof col.render === "function" && shouldIncludeKey(key)) {
      try {
        const v = di !== undefined ? getByPath(row, di) : undefined;
        const rendered = col.render(v, row);
        extractTextFromNode(rendered, parts); // 👈 deep grab text from React nodes
      } catch {
        /* ignore render errors */
      }
    }
  });

  return normalize(parts.join(" | "));
};


  // ### Build a search index map: rowKey -> normalized searchable text
  const searchIndex = React.useMemo(() => {
    const idx = new Map();
    if (!Array.isArray(data)) return idx;

    const opts = {
      includeFields: searchIncludeFields,
      excludeFields: searchExcludeFields,
    };

    for (const row of data) {
      const key = row?.[rowKeyField] ?? row?.key ?? row?.id;
      const hay = buildRowSearchText(row, columns, opts);
      idx.set(key, hay);
    }
    return idx;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, columns, rowKeyField, searchIncludeFields, searchExcludeFields]);

  // Put above filteredData (helpers)

  /** True if char is letter/number in a simple, unicode-friendly way */
  const isAlphaNum = (ch) => !!(ch && /\p{L}|\p{N}/u.test(ch));

  /** Word-boundary-ish match without using \b */
  const wordBoundaryIncludes = (hay, needle) => {
    const idx = hay.indexOf(needle);
    if (idx < 0) return false;
    const before = hay[idx - 1];
    const after = hay[idx + needle.length];
    const leftBoundary = !isAlphaNum(before);
    const rightBoundary = !isAlphaNum(after);
    return leftBoundary && rightBoundary;
  };

  /** Tokenize query: quoted phrases + words; supports wildcards * */
  const tokenize = (q) => {
    const out = [];
    const re = /"([^"]+)"|(\S+)/g;
    let m;
    while ((m = re.exec(q))) out.push(m[1] ?? m[2]);
    return out;
  };

  /** Match a single token/phrase against haystack */
  const matchToken = (hay, token, { wholeWord, isLastToken }) => {
    // wildcard → convert to simple regex (no anchors)
    if (token.includes("*")) {
      const esc = token
        .replace(/[.+?^${}()|[\]\\]/g, "\\$&")
        .replace(/\*/g, ".*");
      const re = new RegExp(esc, "i");
      return re.test(hay);
    }

    // phrase (contains space) → substring
    if (token.includes(" ")) {
      return hay.includes(token);
    }

    // last token while typing → allow prefix match
    if (isLastToken) {
      return hay.includes(token);
    }

    // strict whole-word vs substring
    return wholeWord ? wordBoundaryIncludes(hay, token) : hay.includes(token);
  };

  // ### Filter using the deep index
  // Replace your filteredData useMemo with this:

  const filteredData = React.useMemo(() => {
    if (!searchText) return data;
    const q = normalize(searchText);
    const tokens = tokenize(q).filter(Boolean);

    if (!tokens.length) return data;

    return data.filter((row) => {
      const key = row?.[rowKeyField] ?? row?.key ?? row?.id;
      const hay = searchIndex.get(key) || ""; // already normalized

      // AND across tokens/phrases
      return tokens.every((t, i) =>
        matchToken(hay, t, {
          wholeWord: searchWholeWord, // your prop still respected
          isLastToken: i === tokens.length - 1, // last token = prefix
        })
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchText, rowKeyField, searchIndex, searchWholeWord]);

  // Sort (before pagination)
  const sortedData = React.useMemo(() => {
    const arr = [...filteredData];
    if (!sortField || !sortOrder) return arr;

    const custom = customSorters[sortField];
    const cmp =
      typeof custom === "function"
        ? (a, b) => custom(a, b)
        : (a, b) =>
            baseCompare(getByPath(a, sortField), getByPath(b, sortField));

    arr.sort(cmp);
    if (sortOrder === "descend") arr.reverse();
    return arr;
  }, [filteredData, sortField, sortOrder, customSorters]);

  // Paginate after sorting
  const paginatedData = React.useMemo(
    () =>
      sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sortedData, currentPage, pageSize]
  );

  // Enhance columns with sorter / sortOrder (controlled)
const columnsWithSorting = React.useMemo(() => {
  const baseCols = columns.map((col, idx) => {
    const key = col.key ?? col.dataIndex ?? `col-${idx}`;

    // Decide if this column should be sortable
    const isActionCol = key === "actions" || col.key === "actions";
    const shouldSort =
      !isActionCol && (
        customSorters[key] ||            // explicit custom sorter
        col.sortBy !== undefined ||      // explicit accessor
        col.dataIndex !== undefined ||   // has dataIndex
        col.sortFromRender === true      // asked to sort from render
      );

    // Build sorter fn (custom wins; else generic using getSortValue)
    const sorterFn = customSorters[key]
      ? (a, b) => customSorters[key](a, b)
      : (a, b) => baseCompare(getSortValue(a, col), getSortValue(b, col));

    const isActive =
      String(sortField) === String(col.dataIndex ?? col.key ?? `col-${idx}`);

    return {
      ...col,
      key,                           // ensure a stable key for sorting state
      sorter: shouldSort ? sorterFn : false,
      sortOrder: isActive ? sortOrder : null,
    };
  });

  // Keep your Actions column injection if present
  if (handleEdit || handleDelete || handleAddSlide) {
    const alreadyHasActions = baseCols.some(c => c.key === "actions");
    if (!alreadyHasActions) {
      baseCols.push({
        title: "Actions",
        key: "actions",
        sorter: false,
        render: (_, record) => (
          <div className="action-btns">
            {pageName === "module" && (
              <Tooltip title="Send Notification" placement="top">
                <button className="edit-btn" onClick={() => handleSendNotification?.(record)}>
                  <MdOutlineNotificationsActive />
                </button>
              </Tooltip>
            )}
            {pageName === "chapter" && handleAddSlide && (
              <Tooltip title="Add Slide" placement="top">
                <button className="add-btn-slide" onClick={() => handleAddSlide(record)}>
                  <img src="/add-slide.svg" alt="Add Slide" style={{ width: 25, height: 25, objectFit: "contain" }} />
                </button>
              </Tooltip>
            )}
            {handleEdit && (
              <Tooltip title="Update" placement="top">
                <button className="edit-btn" onClick={() => handleEdit(record)}>
                  <FiEdit2 />
                </button>
              </Tooltip>
            )}
              {pageName === "chapter" && handleCopyChapter && (
              <Tooltip title="Copy Chapter" placement="top">
                <button className="edit-btn" onClick={() => handleCopyChapter(record)}>
                 <IoDuplicateOutline/>
                </button>
              </Tooltip>
            )}
            {handleDelete && (
              <Tooltip title="Delete" placement="top">
                <button className="delete-btn" onClick={() => handleDelete(record)}>
                  <RiDeleteBinLine />
                </button>
              </Tooltip>
            )}
          </div>
        ),
      });
    }
  }

  return baseCols;
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  columns,
  customSorters,
  enableAutoSort, // no longer needed but kept if you still pass it
  handleEdit,
  handleDelete,
  handleAddSlide,
  handleSendNotification,
  pageName,
  sortField,
  sortOrder,
]);


  // Handle AntD table change to update sort state
  const onTableChange = (_, __, sorter) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextField = s?.field ?? s?.columnKey ?? null;
    const nextOrder = s?.order ?? null;
    setSortField(nextField);
    setSortOrder(nextOrder);
    setCurrentPage(1);
  };



  // Put near other helpers
const getSortValue = (row, col) => {
  // Highest priority: explicit sortBy
  if (typeof col.sortBy === "function") return col.sortBy(row);
  if (typeof col.sortBy === "string")  return getByPath(row, col.sortBy);

  // Next: dataIndex
  if (col.dataIndex !== undefined) return getByPath(row, col.dataIndex);

  // Optional: derive from render output when asked
  if (col.sortFromRender && typeof col.render === "function") {
    try {
      const v = col.dataIndex !== undefined ? getByPath(row, col.dataIndex) : undefined;
      const rendered = col.render(v, row);
      if (typeof rendered === "string" || typeof rendered === "number") return rendered;
      // Try to stringify simple nodes
      if (rendered && rendered.props && typeof rendered.props.children === "string") {
        return rendered.props.children;
      }
    } catch { /* ignore */ }
  }

  return undefined;
};



  return (
    <div className="table-wrapper">
      {/* Search bar */}
      {/* Top bar: leftExtra (e.g., your Select) + Search */}
      <div
        className="top-bar"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        {leftExtra ? (
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {leftExtra}
          </div>
        ) : null}

        <Search
          placeholder="Search..."
          allowClear
          enterButton={<FaSearch />}
          onSearch={(value) => setSearchText(value)}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 250, height: 35 }}
          inputStyle={{
            height: "32px",
            padding: "8px 12px",
            fontSize: "14px",
            borderRadius: "6px",
          }}
        />
      </div>

      {/* Table */}
      <Spin spinning={loading} indicator={spinnerIcon}>
        <div className="table-container">
          <Table
            columns={columnsWithSorting}
            dataSource={paginatedData}
            rowSelection={rowSelection}
            rowKey={(record) =>
              record?.[rowKeyField] ?? record?.key ?? record?.id
            }
            pagination={false}
            bordered
            onChange={onTableChange}
          />
        </div>
      </Spin>

      {/* Pagination Controls */}
      <div
        className="pagination-controls"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 16,
          marginTop: 16,
        }}
      >
        <Select
          value={pageSize}
          onChange={(value) => {
            setPageSize(value);
            setCurrentPage(1);
          }}
          options={[
            { label: "5", value: 5 },
            { label: "10", value: 10 },
            { label: "20", value: 20 },
            { label: "30", value: 30 },
            { label: "100", value: 100 },
          ]}
          style={{ width: 120 }}
        />

        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={sortedData.length}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
};

export default TableOne;
